// src/pages/advertisements/AdvertisementsList.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Edit2, Trash2, Image as ImageIcon, Loader2,
  AlertCircle, CheckCircle, X, Link as LinkIcon, Calendar,
} from "lucide-react";
import { advertisementsAPI } from "../../api/advertisements";

/* ─── Toast ─────────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={{ ...styles.toast, ...(type === "error" ? styles.toastError : styles.toastSuccess) }}>
      {type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      {message}
      <button onClick={onClose} style={styles.toastClose}><X size={15} /></button>
    </div>
  );
}

/* ─── تحويل تاريخ ISO لصيغة datetime-local ─────────────────────── */
function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ─── Create/Edit Modal ─────────────────────────────────────────── */
function AdvertisementModal({ ad, onClose, onSaved }) {
  const isEdit = !!ad;
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: ad?.title || "",
    button_name: ad?.button_name || "",
    link_url: ad?.link_url || "",
    order: ad?.order ?? 0,
    is_active: ad?.is_active ?? true,
    start_date: toDatetimeLocal(ad?.start_date),
    end_date: toDatetimeLocal(ad?.end_date),
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(ad?.image || null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
    if (errors.image) setErrors((p) => ({ ...p, image: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!isEdit && !imageFile) errs.image = "صورة الإعلان مطلوبة";
    if (form.link_url && !/^https?:\/\//i.test(form.link_url)) {
      errs.link_url = "الرابط لازم يبدأ بـ http:// أو https://";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        button_name: form.button_name,
        link_url: form.link_url,
        order: form.order,
        is_active: form.is_active,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : "",
        end_date: form.end_date ? new Date(form.end_date).toISOString() : "",
      };
      if (imageFile) payload.image = imageFile;

      if (isEdit) {
        await advertisementsAPI.update(ad.id, payload);
        onSaved("✅ تم تحديث الإعلان بنجاح");
      } else {
        await advertisementsAPI.create(payload);
        onSaved("✅ تم إنشاء الإعلان بنجاح");
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object") setErrors(data);
      else setErrors({ general: "حدث خطأ، حاول مرة أخرى" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{isEdit ? "تعديل الإعلان" : "إضافة إعلان جديد"}</h2>
          <button onClick={onClose} style={styles.iconBtn}><X size={15} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form} dir="rtl">
          {errors.general && <div style={styles.errorBanner}><AlertCircle size={15} />{errors.general}</div>}

          <Field label="صورة الإعلان" required={!isEdit} error={errors.image}>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={styles.imageDropzone}
            >
              {preview ? (
                <img src={preview} alt="preview" style={styles.imagePreview} />
              ) : (
                <div style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                  <ImageIcon size={26} style={{ opacity: 0.5, marginBottom: 6 }} />
                  <div style={{ fontSize: 12 }}>اضغط لاختيار صورة</div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </Field>

          <Field label="عنوان الإعلان">
            <input type="text" name="title" value={form.title} onChange={handleChange} style={styles.input} />
          </Field>

          <Field label="اسم الزر">
            <input type="text" name="button_name" value={form.button_name} onChange={handleChange}
              placeholder="مثال: اشترك الآن" style={styles.input} />
          </Field>

          <Field label="رابط الإعلان" error={errors.link_url}>
            <div style={{ position: "relative" }}>
              <LinkIcon size={14} color="var(--text-secondary)"
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input type="text" name="link_url" value={form.link_url} onChange={handleChange}
                dir="ltr" placeholder="https://example.com"
                style={{ ...styles.input, padding: "9px 36px 9px 12px" }} />
            </div>
          </Field>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Field label="الترتيب">
                <input type="number" name="order" value={form.order} onChange={handleChange} style={styles.input} />
              </Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field label="الحالة">
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  <span>مفعّل</span>
                </label>
              </Field>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Field label="تاريخ البداية (اختياري)">
                <input type="datetime-local" name="start_date" value={form.start_date} onChange={handleChange}
                  style={styles.input} />
              </Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field label="تاريخ النهاية (اختياري)">
                <input type="datetime-local" name="end_date" value={form.end_date} onChange={handleChange}
                  style={styles.input} />
              </Field>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, paddingTop: 6 }}>
            <button type="submit" disabled={loading} style={styles.primaryBtn}>
              {loading && <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />}
              {isEdit ? "حفظ التعديلات" : "إنشاء الإعلان"}
            </button>
            <button type="button" onClick={onClose} style={styles.ghostBtn}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <div style={styles.label}>{label} {required && <span style={{ color: "var(--danger)" }}>*</span>}</div>
      {children}
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );
}

/* ─── Delete Modal ──────────────────────────────────────────────── */
function DeleteModal({ ad, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    try {
      await advertisementsAPI.remove(ad.id);
      onDeleted("🗑️ تم حذف الإعلان بنجاح");
    } catch {
      onClose();
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: 380 }}>
        <div style={{ padding: "32px 28px", textAlign: "center" }} dir="rtl">
          <div style={styles.deleteIconWrap}><Trash2 size={28} color="var(--danger)" /></div>
          <h3 style={styles.modalTitle}>حذف الإعلان</h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, margin: "8px 0 24px" }}>
            هل أنت متأكد من حذف إعلان
            {" "}<strong style={{ color: "var(--text-primary)" }}>{ad.title || `#${ad.id}`}</strong>؟
            لا يمكن التراجع عن ده.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleDelete} disabled={loading} style={styles.dangerSolidBtn}>
              {loading && <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />}
              نعم، احذف
            </button>
            <button onClick={onClose} style={styles.ghostBtn}>إلغاء</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────── */
export default function AdvertisementsList() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const data = await advertisementsAPI.list();
      // الـ endpoint ده array مباشرة (generics.ListCreateAPIView) مفيهوش data wrapper
      setAds(Array.isArray(data) ? data : data.results || []);
    } catch {
      setToast({ message: "فشل تحميل الإعلانات", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const open = (type, a = null) => { setSelected(a); setModal(type); };
  const close = () => { setModal(null); setSelected(null); };
  const handleSaved = (msg) => { close(); setToast({ message: msg, type: "success" }); fetchAds(); };
  const handleDeleted = (msg) => { close(); setToast({ message: msg, type: "success" }); fetchAds(); };

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("ar-EG", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }} dir="rtl">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {(modal === "add" || modal === "edit") && (
        <AdvertisementModal ad={modal === "edit" ? selected : null} onClose={close} onSaved={handleSaved} />
      )}
      {modal === "delete" && <DeleteModal ad={selected} onClose={close} onDeleted={handleDeleted} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)" }}>إدارة الإعلانات</h1>
          <p style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>
            {ads.length} إعلان · بيظهروا كسلايدر جوه التطبيق
          </p>
        </div>
        <button onClick={() => open("add")} style={styles.primaryBtn}>
          <Plus size={15} /> إضافة إعلان
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "var(--accent)" }} />
        </div>
      ) : ads.length === 0 ? (
        <div style={styles.empty}>
          <ImageIcon size={48} style={{ opacity: 0.3, margin: "0 auto 14px", color: "var(--text-secondary)" }} />
          <p style={{ color: "var(--text-primary)", fontWeight: 700 }}>مفيش إعلانات لسه</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
          {ads.map((ad) => {
            const start = formatDate(ad.start_date);
            const end = formatDate(ad.end_date);
            return (
              <div key={ad.id} style={styles.card}>
                <div style={styles.adImageWrap}>
                  {ad.image ? (
                    <img src={ad.image} alt={ad.title} style={styles.adImage} />
                  ) : (
                    <div style={styles.adImagePlaceholder}><ImageIcon size={26} color="var(--text-secondary)" /></div>
                  )}
                  <span style={{ ...styles.badge, ...(ad.is_currently_active ? styles.badgeOn : styles.badgeOff) }}>
                    {ad.is_currently_active ? "شغال دلوقتي" : ad.is_active ? "خارج التوقيت" : "متوقف"}
                  </span>
                </div>

                <div style={{ padding: "12px 4px 0" }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                    {ad.title || `إعلان #${ad.id}`}
                  </div>
                  {ad.button_name && (
                    <div style={{ fontSize: 11.5, color: "var(--accent)", marginTop: 3, fontWeight: 600 }}>
                      زرار: {ad.button_name}
                    </div>
                  )}
                  {ad.link_url && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, color: "var(--text-secondary)" }}>
                      <LinkIcon size={11} />
                      <span style={{ fontSize: 11, direction: "ltr", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ad.link_url}
                      </span>
                    </div>
                  )}
                  {(start || end) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, color: "var(--text-secondary)" }}>
                      <Calendar size={11} />
                      <span style={{ fontSize: 11 }}>
                        {start || "بدون بداية"} → {end || "بدون نهاية"}
                      </span>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 5 }}>
                    الترتيب: {ad.order}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button onClick={() => open("edit", ad)} style={styles.smallBtn}>
                    <Edit2 size={13} /> تعديل
                  </button>
                  <button onClick={() => open("delete", ad)} style={{ ...styles.smallBtn, ...styles.smallBtnDanger }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: "var(--dropdown-bg)", backdropFilter: "blur(28px)", border: "1px solid var(--glass-border)", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid var(--glass-border)" },
  modalTitle: { fontSize: 16, fontWeight: 800, color: "var(--text-primary)" },
  form: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  label: { fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 5 },
  input: { width: "100%", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--glass-border)", background: "var(--glass-bg)", color: "var(--text-primary)", fontSize: 13, outline: "none" },
  fieldError: { fontSize: 11, color: "var(--danger)", marginTop: 4 },
  errorBanner: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--danger-bg)", border: "1px solid var(--danger)", borderRadius: 10, color: "var(--danger)", fontSize: 13 },
  checkboxRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)", cursor: "pointer", padding: "9px 0" },
  primaryBtn: { display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center", background: "linear-gradient(135deg, var(--accent), var(--accent-2))", color: "#fff", border: "none", borderRadius: 12, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", flex: 1 },
  ghostBtn: { background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 12, color: "var(--text-primary)", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "10px 18px", flex: 1 },
  dangerSolidBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flex: 1, padding: "12px 0", background: "var(--danger)", border: "none", borderRadius: 12, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  deleteIconWrap: { width: 64, height: 64, borderRadius: 18, background: "var(--danger-bg)", border: "1px solid var(--danger)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" },
  iconBtn: { width: 32, height: 32, borderRadius: 8, background: "var(--glass-bg)", border: "1px solid var(--glass-border)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-secondary)" },
  toast: { position: "fixed", top: 20, right: 20, zIndex: 999, display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 16, fontSize: 13.5, fontWeight: 600, color: "#fff" },
  toastSuccess: { background: "linear-gradient(135deg,#10b981,#059669)" },
  toastError: { background: "linear-gradient(135deg,#ef4444,#dc2626)" },
  toastClose: { background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.7)", display: "flex" },
  empty: { textAlign: "center", padding: "60px 24px", background: "var(--glass-bg)", border: "1.5px dashed var(--glass-border)", borderRadius: 20 },
  card: { background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 16, padding: 12, backdropFilter: "blur(16px)" },
  imageDropzone: { width: "100%", height: 130, borderRadius: 12, border: "1.5px dashed var(--glass-border)", background: "var(--glass-bg)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden" },
  imagePreview: { width: "100%", height: "100%", objectFit: "cover" },
  adImageWrap: { position: "relative", width: "100%", height: 130, borderRadius: 12, overflow: "hidden", background: "var(--glass-bg)" },
  adImage: { width: "100%", height: "100%", objectFit: "cover" },
  adImagePlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" },
  badge: { position: "absolute", top: 8, left: 8, fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 8 },
  badgeOn: { background: "rgba(52,211,153,0.85)", color: "#fff" },
  badgeOff: { background: "rgba(148,163,184,0.85)", color: "#fff" },
  smallBtn: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 0", borderRadius: 10, border: "1px solid var(--glass-border)", background: "var(--glass-bg)", color: "var(--text-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  smallBtnDanger: { color: "var(--danger)", flex: "0 0 auto", padding: "8px 12px" },
};